import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Unsubscribe — legaldecoder" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: UnsubscribePage,
});

type State =
  | { kind: "loading" }
  | { kind: "valid" }
  | { kind: "already" }
  | { kind: "invalid" }
  | { kind: "submitting" }
  | { kind: "done" };

function UnsubscribePage() {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    setToken(t);
    if (!t) {
      setState({ kind: "invalid" });
      return;
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.valid) setState({ kind: "valid" });
        else if (data.reason === "already_unsubscribed") setState({ kind: "already" });
        else setState({ kind: "invalid" });
      })
      .catch(() => setState({ kind: "invalid" }));
  }, []);

  const confirm = async () => {
    if (!token) return;
    setState({ kind: "submitting" });
    try {
      const r = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await r.json();
      if (data.success || data.reason === "already_unsubscribed") {
        setState({ kind: "done" });
      } else {
        setState({ kind: "invalid" });
      }
    } catch {
      setState({ kind: "invalid" });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto flex min-h-[60vh] max-w-md items-center justify-center px-4 py-16 sm:px-6">
        <div className="w-full rounded-2xl border border-border bg-card p-8 text-center shadow-card">
          {state.kind === "loading" && (
            <>
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
              <p className="mt-4 text-sm text-muted-foreground">Checking your link…</p>
            </>
          )}
          {state.kind === "valid" && (
            <>
              <h1 className="text-2xl font-bold tracking-tight">
                Unsubscribe from legaldecoder emails?
              </h1>
              <p className="mt-3 text-sm text-muted-foreground">
                You can always come back. Important account emails (like password resets) will still
                be sent.
              </p>
              <Button variant="hero" size="lg" className="mt-6 w-full" onClick={confirm}>
                Confirm unsubscribe
              </Button>
            </>
          )}
          {state.kind === "submitting" && (
            <>
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
              <p className="mt-4 text-sm text-muted-foreground">Updating your preferences…</p>
            </>
          )}
          {state.kind === "done" && (
            <>
              <CheckCircle2 className="mx-auto h-10 w-10 text-success" />
              <h1 className="mt-4 text-2xl font-bold tracking-tight">You're unsubscribed</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                We won't send you any more product emails. Sorry to see you go!
              </p>
              <Button variant="outline" className="mt-6" asChild>
                <Link to="/">Back to legaldecoder</Link>
              </Button>
            </>
          )}
          {state.kind === "already" && (
            <>
              <CheckCircle2 className="mx-auto h-10 w-10 text-success" />
              <h1 className="mt-4 text-2xl font-bold tracking-tight">Already unsubscribed</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                You're all set — no further action needed.
              </p>
              <Button variant="outline" className="mt-6" asChild>
                <Link to="/">Back to legaldecoder</Link>
              </Button>
            </>
          )}
          {state.kind === "invalid" && (
            <>
              <h1 className="text-2xl font-bold tracking-tight">This link is invalid</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                It may have expired. Reach out to support@legaldecoder.io if you need help.
              </p>
              <Button variant="outline" className="mt-6" asChild>
                <Link to="/">Back home</Link>
              </Button>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
